/**
 * saveIndicator — the single source of truth for the app-level save-trouble pill.
 *
 * The durable journal (labelJournal.ts) already retains any correction whose write
 * is not yet confirmed, so "there is stranded work" == "journalPendingCount > 0".
 * We suppress the pill while a write is actively in flight ("saving") so a normal,
 * momentary save never flashes it — the entry is journaled for a few ms and then
 * removed on confirmation. It shows for a real failure (status settles to "failed"
 * with the entry retained) and for a boot-stranded entry (status "idle", count > 0).
 */
import { derived, type Readable } from "svelte/store";
import {
  saveStatus,
  journalPendingCount,
  journalReplaying,
  goalSaveStatus,
  goalJournalPendingCount,
} from "./state";

// One pill, one voice: it lights up for stranded work of EITHER kind — a lap-label
// correction (labelJournal) or a ring-goal edit (goalJournal). Each source is
// suppressed while its own write is actively in flight ("saving") so a normal,
// momentary save never flashes it.
export const showSaveTrouble: Readable<boolean> = derived(
  [journalPendingCount, saveStatus, goalJournalPendingCount, goalSaveStatus],
  ([$labelCount, $labelStatus, $goalCount, $goalStatus]) =>
    ($labelCount > 0 && $labelStatus !== "saving") ||
    ($goalCount > 0 && $goalStatus !== "saving"),
);

/**
 * "Try now" button state (Task 4 fix round). replayJournal is reentrancy-safe
 * (an overlapping call shares the in-flight promise), so a repeat tap can't
 * double-dispatch — but harmless isn't the same as visible. While
 * journalReplaying is true the button disables and relabels to "Retrying…";
 * it reverts to "Try now" the moment the pass settles, whether or not any
 * entries remain stranded (showSaveTrouble governs whether the pill itself is
 * still showing at all).
 */
export const retryButtonDisabled: Readable<boolean> = derived(journalReplaying, ($replaying) => $replaying);
export const retryButtonLabel: Readable<string> = derived(
  journalReplaying,
  ($replaying) => ($replaying ? "Retrying…" : "Try now"),
);
